import { useEffect, useRef, useState, type KeyboardEvent } from "react"
import { uploadRefusalMessage } from "@/lib/quotaGate"
import { deleteDocument, followDocuments, uploadDocuments } from "../api"
import type { DocStatus } from "../contract"
import { useDocsStore } from "../docs.store"
import { AllowanceHint } from "@/features/billing/components/AllowanceHint"
import { GateLine, gateAction, useLimitLine } from "../ds/gate"
import { onCompose } from "./compose"
import { docWord } from "./docStatus"
import { AttachIcon, CloseIcon, SendIcon } from "./icons"

const ACCEPT = ".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt,.md"

interface DockProps {
  threadId: string
  followups: string[]
  /** A turn is still running; sending now would be refused. */
  busy: boolean
  onSend: (text: string, docIds: string[]) => void
  /**
   * Other pages supply their own document handling. `uploadFiles` uploads and resolves with the new document ids;
   * `docLookup` says what to show for each id. Without them the dock uses NIRA's document API and store.
   */
  uploadFiles?: (files: File[]) => Promise<string[]>
  docLookup?: Record<string, { filename: string; status: DocStatus }>
  /** Chips before the follow-ups that put a prompt in the box (the rupees prompt, a reading's asks). */
  chips?: { label: string; text: string }[]
  /** Text overrides (accessible name, placeholder, send button, upload error). */
  label?: string
  placeholder?: string
  sendLabel?: string
}

/** Docked composer: follow-up chips, attach, send (Enter sends, Shift+Enter breaks the line). */
export function Dock({ threadId, followups, chips = [], busy, onSend, uploadFiles, docLookup, label = "Ask a follow-up", placeholder = "Ask a follow-up…", sendLabel = "Send follow-up" }: DockProps) {
  const [text, setText] = useState("")
  const [pending, setPending] = useState<string[]>([])
  const [uploadError, setUploadError] = useState("")
  const ta = useRef<HTMLTextAreaElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const file = useRef<HTMLInputElement>(null)
  const niraDocs = useDocsStore((s) => s.docs)
  const docs = docLookup ?? niraDocs
  const watching = useRef<AbortController | null>(null)

  useEffect(() => () => watching.current?.abort(), [])
  // A chip or glance cell elsewhere on the page put a prompt here: fill it in, cursor at the end.
  const fill = (value: string) => {
    setText(value)
    requestAnimationFrame(() => {
      const el = ta.current
      if (!el) return
      el.focus()
      el.setSelectionRange(value.length, value.length)
    })
  }
  useEffect(() => onCompose(fill), [])

  const watch = () => {
    if (watching.current) return
    const c = new AbortController()
    watching.current = c
    followDocuments(threadId, (ev) => useDocsStore.getState().apply(ev), c.signal)
      .catch(() => undefined)
      .finally(() => {
        if (watching.current === c) watching.current = null
      })
  }

  const attach = async (files: FileList | null) => {
    if (!files || !files.length) return
    setUploadError("")
    try {
      if (uploadFiles) {
        const ids = await uploadFiles(Array.from(files))
        setPending((p) => [...p, ...ids])
      } else {
        const rows = await uploadDocuments(threadId, Array.from(files))
        useDocsStore.getState().addUploads(rows)
        setPending((p) => [...p, ...rows.map((r) => r.doc_id)])
        watch()
      }
    } catch (err) {
      setUploadError(uploadRefusalMessage(err) ?? "NIRA could not upload that file.")
    }
    if (file.current) file.current.value = ""
  }

  const remove = (id: string) => {
    setPending((p) => p.filter((x) => x !== id))
    if (!uploadFiles) void deleteDocument(id).catch(() => undefined)
  }

  // Used up with nothing to offer (the first month's 10 a day, Pro's daily limit): said here, no modal.
  const limitLine = useLimitLine("nira.message")
  // A paywall the reader put off with "Not now": the same line instead of the modal, for the rest of the session.
  const [paused, setPaused] = useState("")
  const send = () => {
    const q = text.trim()
    if (!q || busy || limitLine) return
    setPaused("")
    void gateAction("nira.message").then((g) => {
      if (!g.ok) { setPaused(g.line); return }
      onSend(q, pending)
      setText("")
      setPending([])
    })
  }

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      send()
    }
  }

  return (
    <section className={`dock${text ? " fq-typing" : ""}`} aria-label={label}>
      <div className="dockin shiftable">
        {followups.length || chips.length ? (
          <div className="sugs" aria-label="Suggested follow-ups">
            {chips.map((c) => (
              <button key={c.label} type="button" className="sug sug-act" onClick={() => fill(c.text)}>{c.label}</button>
            ))}
            {followups.map((f) => (
              <button key={f} type="button" className="sug" onClick={() => fill(f)}>{f}</button>
            ))}
          </div>
        ) : null}
        {pending.length ? (
          <ul className="dtray" aria-label="Attached for the next question">
            {pending.map((id) => (
              <li className="udoc" key={id}>
                {docs[id]?.filename || "Document"} <span className="st">{docWord(docs[id]?.status)}</span>
                <button type="button" aria-label={`Remove ${docs[id]?.filename || "document"}`} onClick={() => remove(id)}><CloseIcon /></button>
              </li>
            ))}
          </ul>
        ) : null}
        {uploadError ? <p className="note" role="status">{uploadError}</p> : null}
        <div className="dbox" ref={boxRef}>
          <textarea
            ref={ta}
            rows={2}
            aria-label={label}
            placeholder={placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
          />
          <input ref={file} type="file" multiple accept={ACCEPT} hidden onChange={(e) => void attach(e.target.files)} />
          <button className="attach" type="button" aria-label="Attach a file" onClick={() => file.current?.click()}><AttachIcon /></button>
          <button className="send" type="button" aria-label={sendLabel} disabled={busy || !!limitLine || !text.trim()} onClick={send}><SendIcon /></button>
        </div>
        <AllowanceHint metric="nira.message" className="ngl-hint" />
        <GateLine text={limitLine || paused} />
      </div>
    </section>
  )
}
